<script setup lang="ts">
import { app } from '@/composition/app'
import { MediaState } from '@/composition/medias'
import { PostState } from '@/composition/posts'
import { UserState } from '@/composition/users'
import { getAttendeeId, LiveStreamType } from '@/helper/go.live.helper'
import { GoLiveStatus, IPosts } from '@/models/Posts'
import { useGlobalStore } from '@/store/app'
import {
  StreamAttendeeControlBar,
  StreamAttendeePlayer,
  StreamProvider,
  VideoPlayer,
} from '@ghl-revex/assets-drm-client'
import { PlayIcon } from '@gohighlevel/ghl-icons/24/outline'
import { computed, onMounted, onUnmounted, PropType, ref } from 'vue'
import WebcamPostCard from './WebcamPostCard.vue'

const _props = defineProps({
  post: {
    type: Object as PropType<IPosts>,
    required: true,
  },
  customClass: {
    type: String,
    required: false,
    default: '',
  },
})

const appStore = useGlobalStore()

const streamAttendeePlayerRef = ref<InstanceType<
  typeof StreamAttendeePlayer
> | null>(null)

const playerContainerRef = ref<HTMLDivElement | null>(null)
const isVisible = ref<boolean>(false)

let observer: IntersectionObserver | null = null

const joinStream = async () => {
  MediaState.addStream(streamAttendeePlayerRef.value)
  await streamAttendeePlayerRef.value?.joinStream(
    _props.post.metadata?.licenseId || ''
  )
}

const isNotAuthenticatedUser = computed(() => {
  return !app.state.fetching && !appStore.isAuthenticated
})
const leaveStream = async () => {
  MediaState.removeStream(streamAttendeePlayerRef.value as any)
  await streamAttendeePlayerRef.value?.leaveStream()
}

const onRecordingAvailable = () => {
  // TODO: UPDATE THE POST STATUS via api, also update local state\
  if (_props.post?.metadata?.status !== GoLiveStatus.RECORDING_AVAILABLE) {
    PostState.updatePostInPostList(_props.post._id as string, {
      ..._props.post.metadata,
      status: GoLiveStatus.RECORDING_AVAILABLE,
    })
  }
}

const handleClick = (e: Event) => {
  if (
    _props.post?.metadata?.type === LiveStreamType.ROOM &&
    _props.post?.metadata?.status !== GoLiveStatus.RECORDING_AVAILABLE
  ) {
    return
  }
  e.stopPropagation()
}

const onVisible = () => {
  // Once visible, keep it visible (never unmount the player)
  if (!isVisible.value) {
    isVisible.value = true
  }
  // If stream, join the stream
  if (
    _props.post?.metadata?.licenseId &&
    _props.post.metadata?.type === LiveStreamType.STREAM
  ) {
    setTimeout(joinStream, 500)
  }
}

const onHidden = () => {
  // Don't change isVisible - keep component mounted
  // But leave the stream to save resources
  if (
    _props.post?.metadata?.licenseId &&
    _props.post.metadata?.type === LiveStreamType.STREAM
  ) {
    setTimeout(leaveStream, 200)
  }
}

onMounted(() => {
  if (playerContainerRef.value) {
    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // Todo: Add a check if video player then, don't join or leave stream
          if (entry.isIntersecting) {
            onVisible()
          } else {
            onHidden()
          }
        })
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '700px 0px', // Add 700px buffer above and below viewport
      }
    )

    observer.observe(playerContainerRef.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
  leaveStream()
})
</script>

<template>
  <div
    ref="playerContainerRef"
    :class="customClass"
    class="box-border"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keyup.enter="handleClick"
  >
    <!-- if video is available then show video player -->
    <div
      v-if="
        post?.metadata?.licenseId &&
        post?.metadata?.status === GoLiveStatus.RECORDING_AVAILABLE &&
        isVisible
      "
      class="box-border aspect-[16/9]"
    >
      <VideoPlayer
        :license-id="post?.metadata?.licenseId"
        :smart-playback="true"
      />
    </div>

    <!-- if video is not available and type of stream is streaming software then show stream provider -->
    <div
      v-else-if="
        post?.metadata?.licenseId &&
        post.metadata?.type === LiveStreamType.STREAM
      "
      class="box-border aspect-[16/9]"
    >
      <StreamProvider
        v-if="!isNotAuthenticatedUser"
        role="attendee"
        :auto-mute="true"
      >
        <StreamAttendeePlayer
          v-if="post?.metadata?.licenseId"
          ref="streamAttendeePlayerRef"
          class="min-h-[250px]"
          :license-id="post?.metadata?.licenseId"
          :attendee-id="getAttendeeId(post?.metadata?.licenseId || '')"
          :attendee-name="UserState.state.currentUser?.fullName || ''"
          video-fit="contain"
          :auto-join="false"
          @recording-available="onRecordingAvailable"
        >
          <template #controls>
            <StreamAttendeeControlBar
              ref="controlBar"
              bar-background-color="rgba(0, 0, 0, 0.8)"
              icon-color="#ffffff"
              :auto-play="false"
              :auto-mute="true"
              :button-hover-style="{
                background: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)',
              }"
              :volume-thumb-style="{
                background: '#4285f4',
                width: '14px',
                height: '14px',
              }"
            />
          </template>
        </StreamAttendeePlayer>
      </StreamProvider>
      <div v-else>
        <div
          class="relative flex aspect-[16/9] items-center justify-center rounded-lg bg-black"
        >
          <PlayIcon
            class="h-10 w-10 cursor-pointer fill-white text-white"
            @click="appStore.setShowLoginModal(true)"
            @keyup.enter="appStore.setShowLoginModal(true)"
          />
        </div>
      </div>
    </div>
    <!-- if video is not available and type of stream is room then render the room for a second and get participant count -->
    <div
      v-else-if="post?.metadata?.type === LiveStreamType.ROOM"
      class="w-full"
    >
      <WebcamPostCard
        :post="post"
        @recording-available="onRecordingAvailable"
      />
    </div>
  </div>
</template>
<style scoped>
:deep(.stream-video) {
  flex: 1;
}
:deep(.stream-info-overlay) {
  z-index: 20;
}
</style>
